import { ApiResponse, TicketManagerConversation, TicketManagerMessage } from '@vision/ui/interfaces';
import { createHttpExtraOptions } from '../../../utils';
import { TicketManagerApi } from '../ticket-manager.api';
import {
  TicketManagerTicketConversationMessagesArgs,
  TicketManagerTicketConversationMutationResponse,
  TicketManagerTicketConversationsArgs,
  TicketManagerTicketCreateConversationArgs,
  TicketManagerTicketCreateConversationMessageArgs,
} from './ticket-conversations.api.types';
import {
  normalizeTicketManagerTicketConversationMessagesResponse,
  normalizeTicketManagerTicketConversationsResponse,
  TicketManagerConversationCollectionResponseEnvelope,
  TicketManagerMessageCollectionResponseEnvelope,
} from './ticket-conversations.mappers';
import {
  buildTicketManagerTicketConversationMessagesRequest,
  buildTicketManagerTicketConversationsRequest,
  buildTicketManagerTicketCreateConversationMessageRequest,
  buildTicketManagerTicketCreateConversationRequest,
} from './ticket-conversations.routes';

const mutationExtraOptions = createHttpExtraOptions({
  notification: {
    autoHandleSuccess: false,
  },
});

export const TicketConversationsEndpoints = TicketManagerApi.injectEndpoints({
  endpoints: (builder) => ({
    ticketManagerTicketConversations: builder.query<
      ApiResponse<TicketManagerConversation[]>,
      TicketManagerTicketConversationsArgs
    >({
      query: ({ projectId, ticketId }) => buildTicketManagerTicketConversationsRequest({ projectId, ticketId }),
      transformResponse: (response: TicketManagerConversationCollectionResponseEnvelope) =>
        normalizeTicketManagerTicketConversationsResponse(response),
      providesTags: (_result, _error, { ticketId }) => [{ type: 'TicketManagerTicketConversations', id: ticketId }],
    }),
    ticketManagerTicketConversationMessages: builder.query<
      ApiResponse<TicketManagerMessage[]>,
      TicketManagerTicketConversationMessagesArgs
    >({
      query: ({ conversationId, projectId, query, ticketId }) =>
        buildTicketManagerTicketConversationMessagesRequest({
          conversationId,
          projectId,
          query,
          ticketId,
        }),
      transformResponse: (response: TicketManagerMessageCollectionResponseEnvelope) =>
        normalizeTicketManagerTicketConversationMessagesResponse(response),
      providesTags: (_result, _error, { conversationId }) => [
        { type: 'TicketManagerTicketConversationMessages', id: conversationId },
      ],
    }),
    createTicketManagerTicketConversation: builder.mutation<
      TicketManagerTicketConversationMutationResponse,
      TicketManagerTicketCreateConversationArgs
    >({
      query: ({ channelId, content, projectId, subject, ticketId, to }) => ({
        ...buildTicketManagerTicketCreateConversationRequest({
          channelId,
          content,
          projectId,
          subject,
          ticketId,
          to,
        }),
        extraOptions: mutationExtraOptions,
      }),
      invalidatesTags: (_result, _error, { ticketId }) => [{ type: 'TicketManagerTicketConversations', id: ticketId }],
    }),
    createTicketManagerTicketConversationMessage: builder.mutation<
      TicketManagerTicketConversationMutationResponse,
      TicketManagerTicketCreateConversationMessageArgs
    >({
      query: ({ content, conversationId, projectId, ticketId, to }) => ({
        ...buildTicketManagerTicketCreateConversationMessageRequest({
          content,
          conversationId,
          projectId,
          ticketId,
          to,
        }),
        extraOptions: mutationExtraOptions,
      }),
      invalidatesTags: (_result, _error, { conversationId }) => [
        { type: 'TicketManagerTicketConversationMessages', id: conversationId },
      ],
    }),
  }),
});

export const {
  useCreateTicketManagerTicketConversationMessageMutation,
  useCreateTicketManagerTicketConversationMutation,
  useTicketManagerTicketConversationMessagesQuery,
  useTicketManagerTicketConversationsQuery,
} = TicketConversationsEndpoints;
